/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Cell, Grid, Tabs } from 'bold-ui'
import { PageLoading } from 'components/loading'
import { TabLink } from 'components/route'
import { parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalQuery } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { useRouteMatch } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { MedicoesPreNatalAtendimentoModel } from 'view/atendimentos/detail/components/modals/types/model-historicoPreNatal'
import { usePeriodoGestacional } from 'view/atendimentos/detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'

import { convertDadosPreNatal } from '../../../../soap/aside/pre-natal/util/util-acompanhamentoPreNatal'
import { HistoricoMedicoesPreNatalTabRoutes } from './HistoricoMedicoesPreNatalTabRoutes'
import { MedicoesPreNatalTable } from './MedicoesPreNatalTable'
import { MedicaoAntropometricaPreNatal } from './model-medicoesPreNatal'
import {
  getMedicaoForImcReferencia,
  getMedicoesPeso10SemanasAposInicioGestacao,
  getPesoAtendimentoAtual,
} from './util-medicoesPreNatal'

interface HistoricoMedicoesPreNatalViewProps extends MedicoesPreNatalAtendimentoModel {
  prontuarioId: ID
  medicoesAntropometricasPreNatal: MedicaoAntropometricaPreNatal[]
  tipoPreNatal?: TipoPreNatal
}

export function HistoricoMedicoesPreNatalView(props: HistoricoMedicoesPreNatalViewProps) {
  const {
    prontuarioId,
    preNatalAtendimentoAtual,
    dataAtendimento,
    resultadosExamesAtendimentoAtual,
    dumAtendimentoAtual,
    medicoesAntropometricasPreNatal,
    tipoPreNatal,
  } = props

  const match = useRouteMatch()

  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAcompanhamentoPreNatal,
  } = useAcompanhamentoPreNatalQuery({
    variables: { input: prontuarioId },
  })

  const pesoAtendimentoAtual = getPesoAtendimentoAtual(medicoesAntropometricasPreNatal)
  const dumAtendimentoAtualParsed = dumAtendimentoAtual && parseISO(dumAtendimentoAtual)

  const {
    dumStatus: { dumPersistida, hasDumChanged },
    medicoesPreNatal,
  } = useMemo(
    () =>
      convertDadosPreNatal({
        atendimentosProfUltimaGestacao: atendimentosProfUltimaGestacao,
        preNatalAtendimentoAtual,
        dumAtendimentoAtual: dumAtendimentoAtualParsed,
        dataAtendimento,
        pesoAtendimentoAtual,
        tipoPreNatal,
      }),
    [
      atendimentosProfUltimaGestacao,
      preNatalAtendimentoAtual,
      dumAtendimentoAtualParsed,
      dataAtendimento,
      pesoAtendimentoAtual,
      tipoPreNatal,
    ]
  )

  const {
    dataInicioGestacao,
    idadeGestacional,
    hasIgEcoChanged,
    loading: loadingPeriodoGestacional,
  } = usePeriodoGestacional({
    prontuarioId,
    dum: dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    resultadosExamesAtendimentoAtual,
    skip: loadingAcompanhamentoPreNatal,
  })

  const isIgEcografica = !isUndefinedOrNull(idadeGestacional?.ecograficaEmDias)
  const isIgAtualizadaAgora = hasIgEcoChanged || (hasDumChanged && !isIgEcografica)
  const medicaoForImcReferencia = getMedicaoForImcReferencia(dataInicioGestacao, medicoesAntropometricasPreNatal)

  if (loadingAcompanhamentoPreNatal || loadingPeriodoGestacional) return <PageLoading message='Carregando dados' />

  return (
    <Grid>
      <Cell size={12}>
        <MedicoesPreNatalTable
          items={medicoesPreNatal}
          dataInicioGestacao={dataInicioGestacao}
          medicoesAntropometricasPreNatal={medicoesAntropometricasPreNatal}
          isIgEcografica={isIgEcografica}
          showLabelIgRegistradoAgora={isIgAtualizadaAgora}
        />
      </Cell>
      <Cell size={12}>
        <Tabs>
          <TabLink to={`${match.url}/ganho-de-peso`}>Ganho de peso</TabLink>
          <TabLink to={`${match.url}/altura-uterina`}>Altura uterina</TabLink>
        </Tabs>
      </Cell>
      <Cell size={12}>
        <HistoricoMedicoesPreNatalTabRoutes
          match={match}
          medicoesPreNatal={medicoesPreNatal}
          dataInicioGestacao={dataInicioGestacao}
          medicaoForImcReferencia={medicaoForImcReferencia}
          medicoesPeso10SemanasAposInicioGestacao={getMedicoesPeso10SemanasAposInicioGestacao(
            dataInicioGestacao,
            medicoesAntropometricasPreNatal
          )}
        />
      </Cell>
    </Grid>
  )
}
